import React, { useState } from 'react';
import { FlowTypeSelection } from './components/FlowTypeSelection';
import NewModal from '../../Shared/NewModal';
import AddFacilityController from './components/AddFacilityController';
import AddFacilityAdminController from './components/AddFacilityAdminController';
import AddPatientController from './components/AddPatientController';
import { lightBlack, lightGrey } from '../../../mainTheme';

export type FlowType =
  | 'facility'
  | 'facility_admin'
  | 'location_admin'
  | 'patient'
  | 'photo_document';

export const textFieldStyles = {
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    color: lightBlack,
    '&::placeholder': {
      color: lightGrey,
    },
  },
  '& .MuiInputLabel-root': {
    color: lightGrey,
    '&.Mui-focused': {
      color: lightBlack,
    },
  },
};

export const selectFieldStyles = {
  '& .MuiSelect-select': {
    color: lightBlack,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '8px',
  },
  '& + .MuiInputLabel-root': {
    color: `${lightGrey} !important`,
    '&.Mui-focused': {
      color: `${lightBlack} !important`,
    },
  },
  '& .MuiMenuItem-root': {
    color: lightBlack,
  },
};

interface AddNewControllerProps {
  onClose: () => void;
  initialFlowType?: FlowType;
  isFacilityAdmin: boolean;
}

const AddNewController: React.FC<AddNewControllerProps> = ({
  onClose,
  initialFlowType,
  isFacilityAdmin,
}) => {
  const [selectedFlowType, setSelectedFlowType] = useState<FlowType | null>(
    initialFlowType || null,
  );
  const [patientStage, setPatientStage] = useState<string>('initial');

  const onBack = () => {
    setSelectedFlowType(null);
    setPatientStage('initial');
  };

  const handlePatientStageChange = (stage: string) => {
    setPatientStage(stage);
  };

  const renderFlowController = () => {
    if (!selectedFlowType) {
      return (
        <FlowTypeSelection
          onSelect={(type: FlowType) => setSelectedFlowType(type)}
          onCancel={onClose}
          isFacilityAdmin={isFacilityAdmin}
        />
      );
    }

    switch (selectedFlowType) {
      case 'facility':
        return <AddFacilityController onCancel={onClose} onBack={onBack} />;
      case 'facility_admin':
        return <AddFacilityAdminController onClose={onClose} onBack={onBack} />;
      case 'location_admin':
        return (
          <AddFacilityAdminController
            onClose={onClose}
            onBack={onBack}
            isFacilityAdmin={isFacilityAdmin}
          />
        );

      case 'patient':
        return (
          <AddPatientController
            onClose={onClose}
            onBack={onBack}
            onStageChange={handlePatientStageChange}
            isFacilityAdmin={isFacilityAdmin}
          />
        );

      default:
        return null;
    }
  };

  return (
    <NewModal
      showCloseX
      title="Add new"
      onClose={onClose}
      maxWidth={patientStage === 'initial' ? 'xs' : 'sm'}
    >
      {renderFlowController()}
    </NewModal>
  );
};

export default AddNewController;
